import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { InfoIcon, LockKeyholeIcon, TagIcon } from "lucide-react";

export interface AvsSaleTypeStepProps {
  /** Main sale type label (e.g. LLD, Retail). */
  saleTypeLabel?: string;
  /** Explanation shown under the sale type badge. */
  className?: string;
}

const DEFAULT_SALE_TYPE_LABEL = "VDL";
const DEFAULT_DETERMINATION_REASON = ["Canal de vente VDL détecté"];

export function AvsSaleTypeStep({ className }: AvsSaleTypeStepProps) {
  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <TagIcon className="text-primary size-5 shrink-0" aria-hidden />
          Type de Vente
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 pt-6">
        <Alert>
          <InfoIcon className="size-4" aria-hidden />
          <AlertDescription>
            <p>
              Le type de vente est déterminé automatiquement en fonction du type
              de client et du mode de financement sélectionné.
            </p>
            <p>
              Si le type de vente déterminé ne correspond pas à votre attente,
              veuillez vérifier votre brouillon ou contacter le support.
            </p>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            Type de Vente Déterminé
            <LockKeyholeIcon
              className="text-muted-foreground size-4"
              aria-hidden
            />
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-2">
              <Badge className="rounded-md border-transparent px-3 py-1 text-xs font-semibold text-white hover:bg-blue-900/90 dark:bg-blue-950 dark:hover:bg-blue-950/90">
                {DEFAULT_SALE_TYPE_LABEL}
              </Badge>
              <div className="text-muted-foreground flex items-start gap-1.5 text-xs">
                <InfoIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <div className="min-w-0">
                  <p className="mb-1">Raison de la détermination :</p>
                  <ul className="marker:text-muted-foreground list-disc space-y-1 pl-4">
                    {DEFAULT_DETERMINATION_REASON.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <Badge
              variant="outline"
              className="shrink-0 border-green-600 bg-transparent text-green-700 hover:bg-green-50 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-950/30"
            >
              Déterminé automatiquement
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
